import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Instagram, Linkedin, Facebook, Twitter, MessageCircle } from 'lucide-react';

export const Footer = () => {
  const { t } = useTranslation();
  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/d.p.lab/', label: 'Instagram' },
    { icon: Linkedin, href: 'https://www.linkedin.com/company/digitalperformances/', label: 'LinkedIn' },
    { icon: Facebook, href: 'https://www.facebook.com/?locale=it_IT', label: 'Facebook' },
    { icon: Twitter, href: 'https://x.com/DiPerformance', label: 'Twitter/X' },
  ];

  return (
    <footer className="bg-black border-t border-zinc-900 py-12 sm:py-14 md:py-16 px-4 sm:px-6 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl"></div>
      </div>
      
      {/* Giant logo watermark */}
      <div className="absolute right-0 bottom-0 w-[500px] h-auto opacity-[0.02] pointer-events-none">
        <img
          src="https://customer-assets.emergentagent.com/job_eb3e5c94-2f85-4e1f-91b8-a1b5d67e6202/artifacts/hz6a2tqw_Screenshot%202025-11-10%20111436.png"
          alt=""
          className="w-full h-auto object-contain"
        />
      </div>

      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-10 md:gap-12 mb-8 sm:mb-10 md:mb-12">
          {/* Brand */}
          <div className="sm:col-span-2 md:col-span-1">
            <img
              src="https://customer-assets.emergentagent.com/job_eb3e5c94-2f85-4e1f-91b8-a1b5d67e6202/artifacts/hz6a2tqw_Screenshot%202025-11-10%20111436.png"
              alt="Digital Performances Logo"
              className="h-12 sm:h-14 md:h-16 w-auto mb-4 sm:mb-6"
            />
            <p className="text-gray-500 text-sm sm:text-base leading-relaxed">
              {t('footer.brand')}
            </p>
          </div>

          {/* Contatti */}
          <div>
            <h3 className="text-white font-semibold mb-4 sm:mb-6 uppercase tracking-wider text-xs sm:text-sm">{t('footer.contactsTitle')}</h3>
            <div className="space-y-2 sm:space-y-3 text-sm sm:text-base">
              <a
                href="mailto:info@digitalperformances.it"
                className="text-gray-500 hover:text-white transition-colors block"
              >
                info@digitalperformances.it
              </a>
              <a
                href="mailto:assistenza@digitalperformances.it"
                className="text-gray-500 hover:text-white transition-colors block"
              >
                assistenza@digitalperformances.it
              </a>
              <a
                href="https://wa.me/393793460226"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-white transition-colors block"
              >
                WhatsApp: +39 379 346 0226
              </a>
            </div>
          </div>

          {/* Social Media */}
          <div className="sm:col-span-2 md:col-span-1">
            <h3 className="text-white font-semibold mb-4 sm:mb-6 uppercase tracking-wider text-xs sm:text-sm">{t('footer.followTitle')}</h3>
            <div className="flex flex-wrap gap-3 sm:gap-4">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="p-2 sm:p-3 rounded-none bg-zinc-900 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800 text-gray-500 hover:text-white transition-all"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
              
              {/* WhatsApp */}
              <a
                href="https://wa.me/393793460226"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                className="p-2 sm:p-3 rounded-none bg-zinc-900 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800 text-gray-500 hover:text-white transition-all"
              >
                <MessageCircle className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-6 sm:pt-8 border-t border-zinc-900">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600">
            <p>© {new Date().getFullYear()} {t('footer.copyright')}</p>
            <div className="flex flex-wrap gap-4 sm:gap-6 md:gap-8 justify-center">
              <Link to="/privacy-policy" className="hover:text-white transition-colors">
                {t('footer.privacy')}
              </Link>
              <Link to="/cookie-policy" className="hover:text-white transition-colors">
                {t('footer.cookies')}
              </Link>
              <Link to="/terms-conditions" className="hover:text-white transition-colors">
                {t('footer.terms')}
              </Link>
              <Link to="/disclaimer" className="hover:text-white transition-colors">
                {t('footer.disclaimer')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};